/** Centralized debug logging with per-category flag gating, ms timestamps, and throttle support. */
import { DebugConfig, DebugFlagKey } from './DebugConfig';

export type LogCategory = 'ai' | 'pathfinding' | 'combat' | 'skills' | 'physics' | 'spawn';

const LOG_FLAGS: Record<LogCategory, DebugFlagKey> = {
  ai: 'aiLogging',
  pathfinding: 'pathfindingLogging',
  combat: 'combatLogging',
  skills: 'skillLogging',
  physics: 'physicsLogging',
  spawn: 'spawnLogging',
};

/** Format a performance.now() value as a fixed-precision ms timestamp. */
function ts(): string {
  return performance.now().toFixed(1);
}

/** Provides flag-gated console logging by category, with optional throttling. */
export class DebugLog {
  private static throttles = new Map<string, number>();

  /** Log a message under the given category if its flag is enabled. */
  static log(category: LogCategory, msg: string, data?: Record<string, unknown>): void {
    if (!DebugConfig.flags[LOG_FLAGS[category]]) return;
    const prefix = `[${category.toUpperCase()} ${ts()}ms]`;
    if (data) console.log(`${prefix} ${msg}`, data);
    else console.log(`${prefix} ${msg}`);
  }

  /** Return a bound logging function prefixed with the category and a custom label. */
  static logger(category: LogCategory, prefix: string): (msg: string) => void {
    const flagKey = LOG_FLAGS[category];
    return (msg: string) => {
      if (!DebugConfig.flags[flagKey]) return;
      console.log(`[${category.toUpperCase()}:${prefix} ${ts()}ms] ${msg}`);
    };
  }

  /** Log at most once per intervalMs for the given key, suppressing intermediate calls. */
  static throttled(key: string, intervalMs: number, category: LogCategory, msg: string): void {
    if (!DebugConfig.flags[LOG_FLAGS[category]]) return;
    const now = performance.now();
    const last = this.throttles.get(key) ?? 0;
    if (now - last < intervalMs) return;
    this.throttles.set(key, now);
    this.log(category, msg);
  }
}
